import React, { useState, useEffect } from 'react';
import SurgicalHistoryForm from './SurgicalHistoryForm';
import ObstetricHistoryForm from './ObstetricHistoryForm';
import MenstrualHistoryForm from './MenstrualHistoryForm';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Swal from 'sweetalert2';
import useFormDraft from '../../hooks/useFormDraft';
import LoadDraftButton from '../../components/LoadDraftButton';
import {
  Grid,
  Card,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import LoaderOverlay from 'components/LoaderOverlay';
import RichTextEditor from 'components/RichTextEditor';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import NationalityDisplay from 'components/NationalityDisplay';
import { api } from '../../api';

const historyTypes = [
  { value: 'medical', label: 'Medical History' },
  { value: 'surgical', label: 'Surgical History' },
  { value: 'obstetric', label: 'Obstetric History' },
  { value: 'gynecological', label: 'Gynecological History' },
  { value: 'family', label: 'Family History' },
  { value: 'social', label: 'Social History' },
  { value: 'allergy', label: 'Allergy History' },
  { value: 'medication', label: 'Medication History' },
  { value: 'other', label: 'Other' },
];

const defaultGenericHistoryValues = {
  patient_history: '',
  family_history: '',
  allergic_to_drugs: '',
  drug_medical_history: '',
  previous_infertility_treatment: '',
  comorbidities: '',
  notes: '',
};

// A Quill editor that was focused and then cleared still emits markup such
// as "<p><br></p>" / "<p></p>" — strip tags and entities before deciding
// whether a rich-text value counts as empty for required validation.
const isRichTextEmpty = (html) => {
  if (!html) return true;
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim() === '';
};

const defaultSurgicalHistoryValues = {
  surgery: '',
  surgeryDate: '',
  notes: '',
};

// Field slug mapping for generic history form fields
const GENERIC_FIELD_SLUGS = {
  patient_history: 'history-common-params-previous-history',
  family_history: 'history-common-params-family-history',
  allergic_to_drugs: 'history-common-params-allergic-to-drug',
  drug_medical_history: 'history-common-params-drug-medical-history',
  previous_infertility_treatment: 'history-common-params-prev-infertility-treatment',
  comorbidities: 'history-common-params-comorbidities',
  notes: 'history-common-params-note',
};

// Fields the user actually filled whose slug isn't present as an active param
// member in `historyParams`. mapFormDataToParams() drops such fields silently,
// which is unacceptable for a mandatory field like Comorbidities — this lets
// the caller detect it and fail loudly instead. Happens when the page still
// holds a param list fetched before a migration added the member.
const findUnknownFilledSlugs = (formData, historyParams) => {
  const members = historyParams?.param_group_members || [];
  return Object.entries(GENERIC_FIELD_SLUGS)
    .filter(([field, slug]) => {
      const value = formData[field];
      if (!value || isRichTextEmpty(value)) return false;
      return !members.some((p) => p.slug === slug && p.is_active === 'Y');
    })
    .map(([field]) => field);
};

// Field slug mapping for surgical history form fields
const SURGICAL_FIELD_SLUGS = {
  'surgical-history': 'surgical-history',
  history_date: 'history-date',
  'surgical-notes': 'surgical-notes',
  'param-group-member': 'primary-history-common-params-surgical-history',
};
function ContactHistories() {
  const { patient_code: patientCode, couple_code: coupleCode } = useParams();
  const navigate = useNavigate();

  // Patient generic history form
  const {
    control: genericControlPatient,
    handleSubmit: handleGenericSubmitPatient,
    reset: resetGenericPatient,
    formState: { errors: genericErrorsPatient },
  } = useForm({
    defaultValues: defaultGenericHistoryValues,
  });

  // Spouse generic history form
  const {
    control: genericControlSpouse,
    handleSubmit: handleGenericSubmitSpouse,
    reset: resetGenericSpouse,
    formState: { errors: genericErrorsSpouse },
  } = useForm({
    defaultValues: defaultGenericHistoryValues,
  });

  const {
    control: surgicalControl,
    handleSubmit: handleSurgicalSubmit,
    reset: resetSurgical,
    formState: { errors: surgicalErrors },
  } = useForm({
    defaultValues: defaultSurgicalHistoryValues,
  });

  // State management
  const [loading, setLoading] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);
  const [spouseInfo, setSpouseInfo] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState('generic');
  const [opdParams, setOpdParams] = useState([]);
  const [menstrualParams, setMenstrualParams] = useState([]);
  const [genericTab, setGenericTab] = useState(0);
  const [previousSurgicalData, setPreviousSurgicalData] = useState([]);
  const [previousSpouseData, setPreviousSpouseData] = useState([]);
  const [obstetricHistory, setObstetricHistory] = useState(null);
  const [miscarriageHistories, setMiscarriageHistories] = useState([]);
  const [genericParams, setGenericParams] = useState([]);
  const [typeOfMiscarriage, setTypeOfMiscarriage] = useState('');
  const [typeOfPregnancy, setTypeOfPregnancy] = useState('');
  const [originalPatientGenericHistories, setOriginalPatientGenericHistories] = useState([]);
  const [originalSpouseGenericHistories, setOriginalSpouseGenericHistories] = useState([]);
  const [genericHistoryData, setGenericHistoryData] = useState({
    patient_history: '',
    family_history: '',
    allergic_to_drugs: '',
    drug_medical_history: '',
    previous_infertility_treatment: '',
    comorbidities: '',
    notes: '',
  });

  // Draft system for generic history - patient and spouse
  const patientGenericDraft = useFormDraft(`generic_history_patient_${patientInfo?.id}`, 1000);
  const spouseGenericDraft = useFormDraft(`generic_history_spouse_${spouseInfo?.id}`, 1000);

  // Auto-save patient generic history when form changes
  useEffect(() => {
    if (!patientInfo?.id) return;
    const subscription = genericControlPatient._subjects.state.subscribe({
      next: () => {
        const values = genericControlPatient._formValues;
        const hasData = Object.values(values).some(val => val && val.trim && val.trim() !== '');
        if (hasData) {
          patientGenericDraft.autoSave(values);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [patientInfo?.id, genericControlPatient, patientGenericDraft]);

  // Auto-save spouse generic history when form changes
  useEffect(() => {
    if (!spouseInfo?.id) return;
    const subscription = genericControlSpouse._subjects.state.subscribe({
      next: () => {
        const values = genericControlSpouse._formValues;
        const hasData = Object.values(values).some(val => val && val.trim && val.trim() !== '');
        if (hasData) {
          spouseGenericDraft.autoSave(values);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [spouseInfo?.id, genericControlSpouse]);

  // Load patient info and histories on component mount
  useEffect(() => {
    if (patientCode) {
      loadPatientInfo();
    }
  }, [patientCode, coupleCode]);

  useEffect(() => {
    const loadHistories = async () => {
      if (patientInfo?.id) {
        await loadExistingHistories(patientInfo.id, 'patient');
      }
      if (spouseInfo?.id) {
        await loadExistingHistories(spouseInfo.id, 'spouse');
      }
    };
    loadHistories();
  }, [patientInfo?.id, spouseInfo?.id]);

  const loadPatientInfo = async () => {
    console.log('Loading patient info for code:', patientCode);
    try {
      setLoading(true);

      // Fetch patient details by external code
      const patientResponse = await api.contacts.getPatientDetails(patientCode);

      if (patientResponse?.data?.contact) {
        const patient = patientResponse.data.contact;
        const spouse = patientResponse.data?.spouse;

        const patientData = {
          id: patient.id,
          name: patient.full_name,
          external_code: patient.external_code,
          couple_code: patientResponse.data?.couple_info?.external_code,
          gender: patient.gender,
          phone_number: patientResponse.data?.phones[0]?.phone_number,
          email: patient.email,
          occupation: patient.occupation,
          nationality_id: patient.irm_countries_id,
          nationality: patient?.nationality,
          marital_status: patient.marital_status,
          married_for: patient.married_for,
          trying_for: patient.trying_for,
        };
        setPatientInfo(patientData);

        // Handle spouse data
        let spouseData = {
          id: null,
          name: '',
          external_code: '',
          couple_code: '',
          gender: '',
          phone_number: '',
          email: '',
          occupation: '',
          nationality_id: '',
          nationality: '',
          marital_status: '',
          married_for: '',
          trying_for: '',
        };

        if (spouse?.contact) {
          spouseData = {
            id: spouse.contact.id,
            name: spouse.contact.full_name,
            external_code: spouse.contact.external_code,
            couple_code: patientResponse.data?.couple_info?.external_code || '',
            gender: spouse.contact.gender,
            phone_number: spouse?.phones?.[0]?.phone_number || '',
            email: spouse.contact.email_address || '',
            occupation: spouse.contact.occupation || '',
            nationality_id: spouse.contact.irm_countries_id || '',
            nationality: spouse.contact.nationality || '',
            marital_status: spouse.contact.marital_status || '',
            married_for: spouse.contact.married_for || '',
            trying_for: spouse.contact.trying_for || '',
          };
        }
        setSpouseInfo(spouseData);

        // Fetch OPD params and existing histories after getting patient info
        await loadOpdParams();
        // await loadExistingHistories(patient.id);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Patient Not Found',
          text: 'Patient not found with the provided code',
        });
      }
    } catch (error) {
      console.error('Error loading patient info:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error Loading Patient',
        text: error.response?.data?.message || 'Failed to load patient information',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadOpdParams = async () => {
    try {
      const paramsResponse = await api.opd.getHistoryParams();
      console.log('Raw OPD params response:', paramsResponse);
      if (paramsResponse?.data?.primary_history_params?.param_group_members) {
        // Find the Surgical History group
        const surgicalHistory = paramsResponse.data.primary_history_params.param_group_members.find(
          (member) => member.name === 'Surgical History',
        );
        console.log('Surgical History group:', surgicalHistory);

        if (surgicalHistory?.param_group_member_options) {
          // Transform options into the format needed for the dropdown
          const surgicalOptions = surgicalHistory.param_group_member_options.map((option) => ({
            value: option.option_value.toLowerCase(),
            label: option.option_value,
            param_group_member_id: option.irm_param_group_members_id,
            param_group_member_option_id: option.id,
            param_group_id: surgicalHistory.irm_param_groups_id,
          }));
          console.log('Transformed surgical options:', surgicalOptions);
          setOpdParams(surgicalOptions);
        } else {
          console.warn('No surgical history options found');
          setOpdParams([]);
        }
      }

      // Handle menstrual history params
      if (paramsResponse?.data?.menstrual_history_params) {
        console.log('Menstrual History params:', paramsResponse.data.menstrual_history_params);
        setMenstrualParams(paramsResponse.data.menstrual_history_params);
      } else {
        console.warn('No menstrual history params found');
        setMenstrualParams([]);
      }

      // if(paramsResponse?.data?.history_params?.param_group_member) {
      //   const historyOptions = paramsResponse.data.history_params.map((option) => ({
      //     value: option.option_value.toLowerCase(),
      //     label: option.option_value,
      //     param_group_member_id: option.irm_param_group_members_id,
      //     param_group_member_option_id: option.id,
      //     param_group_id: option.irm_param_groups_id,
      //   }));
      //   console.log('Transformed history options:', historyOptions);
      //   setGenericParams(historyOptions);
      // }
      const general_history_params = paramsResponse?.data?.history_params?.param_group_members || [];
      console.log('Generic history params:==========', general_history_params);
      const primaryOptions = general_history_params.map(opt => {
          console.log('Generic param_group_member option XXXXXXXX:', opt);
          return {
          value: opt.name,
          label: opt.name,
          param_group_member_id: opt.id,
          param_group_member_option_id: null,
          param_group_id: opt.irm_param_groups_id
          };
      });

      setGenericParams(paramsResponse?.data?.history_params);
      // console.log(primaryOptions);
      console.log('Generic Params: AAAAAAAAAAAAAA', primaryOptions);

      // Type of Miscarriage
      const obstetric_history = paramsResponse?.data?.obsteric_history_params?.param_group_members || [];
      // console.log('Obstetric History Params:', obstetric_history);
      const obstetric_history_params = obstetric_history.flatMap(opt => {
        if (opt.slug === 'obsteric-history-params-type-of-miscarriage' && Array.isArray(opt.param_group_member_options)) {
          return opt.param_group_member_options.map(option => ({
        value: option.option_value,
        label: option.option_value,
        param_group_member_id: opt.id,
        param_group_member_option_id: option.id,
        param_group_id: opt.irm_param_groups_id
          }));
        } else {
          return {};
        }
      });

      setTypeOfMiscarriage(obstetric_history_params);
      console.log('Type of Miscarriage:', obstetric_history_params);

      // Type of Pregnancy
      const pregnancy_type_params = obstetric_history.flatMap(opt => {
        if (opt.slug === 'obsteric-history-params-pregnency-type' && Array.isArray(opt.param_group_member_options)) {
          return opt.param_group_member_options.map(option => ({
            value: option.option_value,
            label: option.option_value,
            param_group_member_id: opt.id,
            param_group_member_option_id: option.id,
            param_group_id: opt.irm_param_groups_id
          }));
        } else {
          return {};
        }
      });

      setTypeOfPregnancy(pregnancy_type_params);
      console.log('Type of Pregnancy:', pregnancy_type_params);

    } catch (error) {
      console.error('Error loading OPD params:', error);
    }
  };

  // Re-fetch the Generic History param list right before a save so a member
  // added since the page loaded (e.g. a freshly-run migration) is available
  // for slug -> id resolution. Falls back to whatever is already in state.
  const getFreshHistoryParams = async () => {
    try {
      const resp = await api.opd.getHistoryParams();
      const historyParams = resp?.data?.history_params;
      if (historyParams?.param_group_members) {
        setGenericParams(historyParams);
        return historyParams;
      }
    } catch (err) {
      console.error('Failed to refresh history params before save:', err);
    }
    return genericParams;
  };

  const loadExistingHistories = async (contactId, type) => {
    try {
      console.log(`Loading ${type} histories for contact ID:`, contactId);
      const historiesResponse = await api.opd.getContactHistory(contactId);
      console.log(`${type} histories response:`, historiesResponse);
      let histories = historiesResponse?.data?.contact_histories || [];
      let obstetric_history = historiesResponse?.data?.obsteric_history || null;
      let miscarriage_histories = historiesResponse?.data?.miscarriage_histories || [];
      console.log(`Loaded obstetric_history histories for ${type}:`, obstetric_history);

      // Set obstetric and miscarriage histories
      if (type === 'patient') {
        setObstetricHistory(obstetric_history);
        setMiscarriageHistories(miscarriage_histories);
      }

      // Process all histories for form population
      if (histories.length > 0) {
        const formData = { ...defaultGenericHistoryValues };
        const genericHistories = [];
        
        histories.forEach((historyItem) => {
          if (historyItem.param_group_member?.slug) {
            // Map the history values to form fields
            Object.entries(GENERIC_FIELD_SLUGS).forEach(([fieldName, fieldSlug]) => {
              if (fieldSlug === historyItem.param_group_member.slug) {
                formData[fieldName] = historyItem.value || '';
                genericHistories.push({
                  id: historyItem.id,
                  contact_id: historyItem.irm_contacts_id,
                  param_group_id: historyItem.irm_param_groups_id,
                  param_group_member_id: historyItem.irm_param_group_members_id,
                  fieldName: fieldName,
                  slug: fieldSlug,
                  value: historyItem.value || ''
                });
              }
            });
          }
        });

        // Store original histories for comparison
        if (type === 'patient') {
          setOriginalPatientGenericHistories(genericHistories);
        } else if (type === 'spouse') {
          setOriginalSpouseGenericHistories(genericHistories);
        }

        // Reset the appropriate form with populated data
        console.log(`Setting ${type} generic form data:`, formData);
        if (type === 'patient') {
          resetGenericPatient(formData);
        } else if (type === 'spouse') {
          resetGenericSpouse(formData);
        }
      }

      // Extract and set surgical histories
      const surgicalData = histories
        .filter(
          (history) =>
            history.param_group_member?.slug === 'primary-history-common-params-surgical-history',
        )
        .map((history) => ({
          id: history.id,
          surgery: history.param_group_member_option?.value || history.value || '',
          notes: history.remarks || '',
          history_date: history.history_date || '',
          contact_id: history.irm_contacts_id || contactId,
          param_group_id: history.irm_param_groups_id || '3',
          param_group_member_id: history.irm_param_group_members_id || '',
          param_group_member_option_id: history.irm_param_group_member_options_id || null
        }));

      if (type === 'patient') {
        console.log('Setting patient surgical histories:', surgicalData);
        setPreviousSurgicalData(surgicalData);
      } else if (type === 'spouse') {
        console.log('Setting spouse surgical histories:', surgicalData);
        setPreviousSpouseData(surgicalData);
      }

    } catch (error) {
      console.error('Error loading existing histories:', error);
    }
  };

  const mapFormDataToParams = (formData, fieldSlugs, contactId, paramsOverride) => {
    const historyParams = paramsOverride || genericParams;
    console.log('Mapping form data to params:', formData, fieldSlugs, contactId);
    if (!opdParams) {
      console.warn('OPD params not loaded or not an array:', opdParams);
      return [];
    }
    console.log('OPD params loaded successfully:', opdParams);
    console.log('Generic Params loaded successfully:>>>>>>>>>>>', genericParams);
    const historyArray = [];

    // Extract all param group members from all parameter groups
    const allParamMembers = [];
    for (const paramGroupindex in opdParams) {
      const paramGroup = opdParams[paramGroupindex];
      if (paramGroup) {
        if (paramGroup.param_group_members && Array.isArray(paramGroup.param_group_members)) {
          allParamMembers.push(...paramGroup.param_group_members);
        }
      }
    }

    const allGenericMembers = [];

    console.log('All parameter members:********', allParamMembers);
    console.log('All generic members:********', allGenericMembers);
    // Get current date for history_date
    const currentDate = new Date().toISOString().split('T')[0];

    Object.entries(formData).forEach(([fieldName, fieldValue]) => {
      const slug = fieldSlugs[fieldName];
      if (slug) {
        // Find the parameter member by slug
        const paramMember = historyParams?.param_group_members?.find((p) => p.slug === slug && p.is_active === 'Y');

        if (paramMember) {
          // Only add to array if fieldValue has actual content (not empty/blank)
          // This allows blank values to delete old records without saving new ones
          if (fieldValue && fieldValue.trim() && fieldValue.trim() !== '<p></p>' && fieldValue.trim() !== '<p><br></p>') {
            historyArray.push({
              contact_id: contactId,
              param_group_id: paramMember.irm_param_groups_id,
              param_group_member_id: paramMember.id,
              param_group_member_option_id: null,
              name: paramMember.name,
              value: fieldValue,
              history_date: currentDate,
              remarks: null,
              private_note: null,
            });
          }
        }
      }
    });
    console.log('Mapped history array:*********', historyArray);
    return historyArray;
  };

  const populateHistoryForms = (patientHistories, spouseHistories) => {
    const genericFormDataPatient = { ...defaultGenericHistoryValues };
    const genericFormDataSpouse = { ...defaultGenericHistoryValues };
    const surgicalFormData = { ...defaultSurgicalHistoryValues };

    // Process patient histories
    patientHistories.forEach((historyItem) => {
      if (historyItem.param_group_member && historyItem.value) {
        const memberSlug = historyItem.param_group_member.slug;
        const value = historyItem.value;

        // Map to generic patient form fields
        Object.entries(GENERIC_FIELD_SLUGS).forEach(([fieldName, fieldSlug]) => {
          if (fieldSlug === memberSlug) {
            genericFormDataPatient[fieldName] = value;
          }
        });

        // Prepare surgical history data for patient
        if (
          historyItem.param_group_member &&
          historyItem.param_group_member.slug === 'primary-history-common-params-surgical-history'
        ) {
          const surgery = historyItem.param_group_member_option?.value || historyItem.value || '';
          const notes = historyItem.remarks || '';

          surgicalFormData.surgery = surgery;
          surgicalFormData.notes = notes;
          surgicalFormData.history_date = historyItem.history_date || '';
        }
      }
    });

    // Process spouse histories
    spouseHistories.forEach((historyItem) => {
      if (historyItem.param_group_member && historyItem.value) {
        const memberSlug = historyItem.param_group_member.slug;
        const value = historyItem.value;

        // Map to generic spouse form fields
        Object.entries(GENERIC_FIELD_SLUGS).forEach(([fieldName, fieldSlug]) => {
          if (fieldSlug === memberSlug) {
            genericFormDataSpouse[fieldName] = value;
          }
        });

        // Prepare surgical history data for spouse
        if (
          historyItem.param_group_member &&
          historyItem.param_group_member.slug === 'primary-history-common-params-surgical-history'
        ) {
          const surgery = historyItem.param_group_member_option?.value || historyItem.value || '';
          const notes = historyItem.remarks || '';

          previousSpouseData.surgery = surgery;
          previousSpouseData.notes = notes;
          previousSpouseData.history_date = historyItem.history_date || '';
        }
        
      }
    });

    // Reset forms with the populated data
    setTimeout(() => {
      if (patientInfo?.id) {
        resetGenericPatient(genericFormDataPatient);
      }
      if (spouseInfo?.id) {
        resetGenericSpouse(genericFormDataSpouse);
      }
      resetSurgical(surgicalFormData);
    }, 0);
  };

  const onGenericSubmitPatient = async (data) => {
    return await patientGenericDraft.handleSubmitWithDraft(async (formData) => {
      try {
        setLoading(true);

        if (!opdParams) {
          Swal.fire({
            icon: 'error',
            title: 'System Error',
            text: 'System parameters not loaded. Please refresh the page and try again.'
          });
          return { success: false };
        }

        console.log('Patient Generic History data:', formData);
        console.log('Generic field slugs:', GENERIC_FIELD_SLUGS);
        console.log('Original patient histories:', originalPatientGenericHistories);
        
        // Step 1: Find fields that had values but are now blank and delete them
        const fieldsToDelete = [];
        originalPatientGenericHistories.forEach(originalHistory => {
          const currentValue = formData[originalHistory.fieldName];
          const isBlank = !currentValue || !currentValue.trim() || currentValue.trim() === '<p></p>' || currentValue.trim() === '<p><br></p>';
          
          if (originalHistory.value && isBlank) {
            // This field had a value before but is now blank - delete it
            fieldsToDelete.push({
              contact_id: originalHistory.contact_id,
              param_group_id: originalHistory.param_group_id,
              param_group_member_id: originalHistory.param_group_member_id,
              contact_history_id: originalHistory.id
            });
          }
        });
        
        // Delete the cleared fields
        for (const deletePayload of fieldsToDelete) {
          try {
            await api.opd.deleteContactHistory(deletePayload);
            console.log('Deleted history record:', deletePayload.contact_history_id);
          } catch (deleteError) {
            console.error('Error deleting history record:', deletePayload.contact_history_id, deleteError);
          }
        }
        
        // Step 2: Save only non-blank fields (refresh params first so a
        // newly-migrated member like Comorbidities resolves)
        const historyParams = await getFreshHistoryParams();
        const unknownFields = findUnknownFilledSlugs(formData, historyParams);
        if (unknownFields.length > 0) {
          Swal.fire({
            icon: 'error',
            title: 'Cannot Save',
            text: `These history fields aren't recognised by the server yet: ${unknownFields.join(', ')}. Please hard-refresh the page and try again.`,
          });
          return { success: false };
        }
        const historyArray = mapFormDataToParams(formData, GENERIC_FIELD_SLUGS, patientInfo?.id, historyParams);

        if (historyArray.length === 0 && fieldsToDelete.length === 0) {
          console.log('No changes to save');
          Swal.fire({
            icon: 'info',
            title: 'No Changes',
            text: 'No changes detected in the form'
          });
          return { success: false };
        }

        if (historyArray.length > 0) {
          const createResponse = await api.opd.createContactHistory(historyArray);
          if (!createResponse?.data) {
            throw new Error('Failed to save history');
          }
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Patient Generic History saved successfully'
        });
        
        try {
          await loadExistingHistories(patientInfo?.id, 'patient');
        } catch (loadError) {
          console.error('Error reloading histories after save:', loadError);
        }
        
        return { success: true };
      } catch (error) {
        console.error('Error saving generic history:', error);
        Swal.fire({
          icon: 'error',
          title: 'Save Failed',
          text: error.response?.data?.message || 'Failed to save generic history'
        });
        return { success: false };
      } finally {
        setLoading(false);
      }
    }, data);
  };

  const onGenericSubmitSpouse = async (data) => {
    return await spouseGenericDraft.handleSubmitWithDraft(async (formData) => {
      try {
        setLoading(true);

        if (!opdParams) {
          Swal.fire({
            icon: 'error',
            title: 'System Error',
            text: 'System parameters not loaded. Please refresh the page and try again.'
          });
          return { success: false };
        }

        console.log('Spouse Generic History data:', formData);
        console.log('Original spouse histories:', originalSpouseGenericHistories);

        // Step 1: Find fields that had values but are now blank and delete them
        const fieldsToDelete = [];
        originalSpouseGenericHistories.forEach(originalHistory => {
          const currentValue = formData[originalHistory.fieldName];
          const isBlank = !currentValue || !currentValue.trim() || currentValue.trim() === '<p></p>' || currentValue.trim() === '<p><br></p>';
          
          if (originalHistory.value && isBlank) {
            // This field had a value before but is now blank - delete it
            fieldsToDelete.push({
              contact_id: originalHistory.contact_id,
              param_group_id: originalHistory.param_group_id,
              param_group_member_id: originalHistory.param_group_member_id,
              contact_history_id: originalHistory.id
            });
          }
        });

        // Delete the cleared fields
        for (const deletePayload of fieldsToDelete) {
          try {
            await api.opd.deleteContactHistory(deletePayload);
            console.log('Deleted spouse history record:', deletePayload.contact_history_id);
          } catch (deleteError) {
            console.error('Error deleting spouse history record:', deletePayload.contact_history_id, deleteError);
          }
        }

        // Step 2: Save only non-blank fields (refresh params first so a
        // newly-migrated member like Comorbidities resolves)
        const historyParams = await getFreshHistoryParams();
        const unknownFields = findUnknownFilledSlugs(formData, historyParams);
        if (unknownFields.length > 0) {
          Swal.fire({
            icon: 'error',
            title: 'Cannot Save',
            text: `These history fields aren't recognised by the server yet: ${unknownFields.join(', ')}. Please hard-refresh the page and try again.`,
          });
          return { success: false };
        }
        const historyArray = mapFormDataToParams(formData, GENERIC_FIELD_SLUGS, spouseInfo?.id, historyParams);

        if (historyArray.length === 0 && fieldsToDelete.length === 0) {
          console.log('No changes to save');
          Swal.fire({
            icon: 'info',
            title: 'No Changes',
            text: 'No changes detected in the form'
          });
          return { success: false };
        }

        if (historyArray.length > 0) {
          const createResponse = await api.opd.createContactHistory(historyArray);
          if (!createResponse?.data) {
            throw new Error('Failed to save history');
          }
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Spouse Generic History saved successfully'
        });
        
        try {
          await loadExistingHistories(spouseInfo?.id, 'spouse');
        } catch (loadError) {
          console.error('Error reloading histories after save:', loadError);
        }
        
        return { success: true };
      } catch (error) {
        console.error('Error saving spouse generic history:', error);
        Swal.fire({
          icon: 'error',
          title: 'Save Failed',
          text: error.response?.data?.message || 'Failed to save spouse generic history'
        });
        return { success: false };
      } finally {
        setLoading(false);
      }
    }, data);
  };

  const onSurgicalSubmit = async (data, type) => {
    try {
      setLoading(true);

      if (!opdParams || opdParams.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'System Error',
          text: 'System parameters not loaded. Please refresh the page and try again.'
        });
        return;
      }

      const currentDate = new Date().toISOString().split('T')[0];

      // Create history array with the correct format
      const historyArray = data
        .map((row) => {
          const selectedSurgery = opdParams.find((opt) => opt.value === row['surgical-history']);

          return {
            contact_id: type === 'spouse' ? spouseInfo?.id : patientInfo?.id,
            param_group_id: selectedSurgery?.param_group_id || 3,
            param_group_member_id: selectedSurgery?.param_group_member_id || 10,
            param_group_member_option_id: selectedSurgery?.param_group_member_option_id || null,
            name: 'Surgical History',
            value: row['surgical-history'],
            history_date: row['history_date'] || null,
            remarks: row['surgical-notes'] || null,
            private_note: null,
            originalDate: row['history_date'], // Keep track of original date for validation
          };
        })
        .filter((item) => item.value); // Only require surgery type, date is now optional

      if (historyArray.length === 0) {
        console.warn('Z No valid data to save:', historyArray);
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'No valid data to save. Please check if all required fields are filled.'
        });
        return;
      }

      // Check if there are any entries without dates and show confirmation
      const entriesWithoutDate = historyArray.filter(item => !item.originalDate);
      if (entriesWithoutDate.length > 0) {
        // Temporarily hide loader to prevent z-index conflicts
        setLoading(false);
        const surgeryNames = entriesWithoutDate.map(item => item.value).join(', ');
        const confirmResult = await Swal.fire({
          title: 'Surgery Date Missing',
          html: `<p>The following surgical history entries don't have dates:</p><p><strong>${surgeryNames}</strong></p><p>Are you sure you want to save without surgery dates?</p>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#00798E',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Yes, save without dates',
          cancelButtonText: 'Cancel and add dates',
          reverseButtons: true,
          zIndex: 10000,
          backdrop: true,
          allowOutsideClick: false,
          customClass: {
            container: 'swal-high-z-index'
          }
        });

        if (!confirmResult.isConfirmed) {
          // User cancelled, keep loader off
          return;
        }
        
        // User confirmed, re-enable loader for API call
        setLoading(true);
      }

      // Remove originalDate field before sending to API
      const cleanHistoryArray = historyArray.map(({ originalDate, ...rest }) => rest);

      const response = await api.opd.createContactHistory(cleanHistoryArray);
      if (response?.data) {
        // Get the new history IDs from the response
        const newHistories = response.data.map((item) => ({
          id: item.id,
          surgery: item.param_group_member_option?.value || item.value || '',
          notes: item.remarks || '',
          history_date: item.history_date || '',
          contact_id: item.irm_contacts_id || (type === 'spouse' ? spouseInfo?.id : patientInfo?.id),
          param_group_id: item.irm_param_groups_id || '3',
          param_group_member_id: item.irm_param_group_members_id || '',
          param_group_member_option_id: item.irm_param_group_member_options_id || null
        }));

        // Update the appropriate state immediately
        if (type === 'spouse') {
          setPreviousSpouseData(prevData => [...prevData, ...newHistories]);
        } else {
          setPreviousSurgicalData(prevData => [...prevData, ...newHistories]);
        }

        // Reset the form
        resetSurgical({
          'surgical-history': '',
          'history_date': '',
          'surgical-notes': '',
          'param_group_member_id': '',
          'param_group_member_option_id': '',
          'param_group_id': ''
        });

        // Load all histories to ensure complete sync (wrapped in try-catch to not interfere with success message)
        try {
          await loadExistingHistories(type === 'spouse' ? spouseInfo?.id : patientInfo?.id, type);
        } catch (loadError) {
          console.error('Error reloading histories after save:', loadError);
          // Don't show error to user, the save was successful
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `${type === 'spouse' ? 'Spouse' : 'Patient'} Surgical History added successfully`
        });
      }
    } catch (error) {
      console.error('Error saving surgical history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: error.response?.data?.message || 'Failed to save surgical history'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearGenericForm = () => {
    resetGenericPatient(defaultGenericHistoryValues);
    resetGenericSpouse(defaultGenericHistoryValues);
  };

  const handleClearSurgicalForm = () => {
    resetSurgical(defaultSurgicalHistoryValues);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleGenericTabChange = (event, newValue) => setGenericTab(newValue);

  // Menstrual history only applies to whichever of patient/spouse is female
  const menstrualContactInfo =
    patientInfo?.gender?.toLowerCase() === 'f'
      ? patientInfo
      : spouseInfo?.gender?.toLowerCase() === 'f'
        ? spouseInfo
        : null;

  const onDeletePatient = async (id) => {
    try {
      setLoading(true);
      const response = await api.opd.deleteContactHistory(id);
      if (response?.data) {
        // Immediately update the UI by removing the deleted row from state
        setPreviousSurgicalData(prevData => prevData.filter(item => item.id !== id));
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: 'Patient surgical history deleted successfully'
        });
        // Refresh all histories to ensure sync with server
        try {
          await loadExistingHistories(patientInfo?.id, 'patient');
        } catch (loadError) {
          console.error('Error reloading histories after delete:', loadError);
        }
      }
    } catch (error) {
      console.error('Error deleting patient surgical history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: error.response?.data?.message || 'Failed to delete patient surgical history'
      });
    } finally {
      setLoading(false);
    }
  };

  const onDeleteSpouse = async (id) => {
    try {
      setLoading(true);
      const response = await api.opd.deleteContactHistory(id);
      if (response?.data) {
        // Immediately update the UI by removing the deleted row from state
        setPreviousSpouseData(prevData => prevData.filter(item => item.id !== id));
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: 'Spouse surgical history deleted successfully'
        });
        // Refresh all histories to ensure sync with server
        try {
          await loadExistingHistories(spouseInfo?.id, 'spouse');
        } catch (loadError) {
          console.error('Error reloading histories after delete:', loadError);
        }
      }
    } catch (error) {
      console.error('Error deleting spouse surgical history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: error.response?.data?.message || 'Failed to delete spouse surgical history'
      });
    } finally {
      setLoading(false);
    }
  };

  const onEditPatient = async (updatedRecord) => {
    try {
      console.log('onEditPatient called with:', updatedRecord);
      
      // Check if date is missing and show confirmation
      if (!updatedRecord.history_date) {
        const confirmResult = await Swal.fire({
          title: 'Missing Surgery Date',
          text: 'You are updating surgical history without a surgery date. Do you want to continue?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#00798E',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, continue without date',
          cancelButtonText: 'Cancel'
        });
        
        if (!confirmResult.isConfirmed) {
          return;
        }
      }
      
      setLoading(true);
      const updatePayload = {
        contact_history_id: updatedRecord.id,
        value: updatedRecord.value || updatedRecord.surgery?.toUpperCase(),
        history_date: updatedRecord.history_date || null,
        remarks: updatedRecord.notes,
        param_group_member_id: updatedRecord.param_group_member_id,
        param_group_member_option_id: updatedRecord.param_group_member_option_id,
      };
      
      console.log('Update payload:', updatePayload);
      console.log('Calling api.opd.updateContactHistory...');
      
      const response = await api.opd.updateContactHistory(updatePayload);
      console.log('API response:', response);
      
      if (response?.data) {
        // Update the UI immediately
        setPreviousSurgicalData(prevData => 
          prevData.map(item => item.id === updatedRecord.id ? {
            ...item,
            surgery: updatedRecord.surgery,
            history_date: updatedRecord.history_date,
            notes: updatedRecord.notes,
            param_group_member_id: updatedRecord.param_group_member_id,
            param_group_member_option_id: updatedRecord.param_group_member_option_id,
          } : item)
        );
        
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Patient surgical history updated successfully'
        });
        
        // Refresh to ensure sync
        try {
          await loadExistingHistories(patientInfo?.id, 'patient');
        } catch (loadError) {
          console.error('Error reloading histories after update:', loadError);
        }
      }
    } catch (error) {
      console.error('Error updating patient surgical history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update patient surgical history'
      });
    } finally {
      setLoading(false);
    }
  };

  const onEditSpouse = async (updatedRecord) => {
    try {
      console.log('onEditSpouse called with:', updatedRecord);
      
      // Check if date is missing and show confirmation
      if (!updatedRecord.history_date) {
        const confirmResult = await Swal.fire({
          title: 'Missing Surgery Date',
          text: 'You are updating spouse surgical history without a surgery date. Do you want to continue?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#00798E',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, continue without date',
          cancelButtonText: 'Cancel'
        });
        
        if (!confirmResult.isConfirmed) {
          return;
        }
      }
      
      setLoading(true);
      const updatePayload = {
        contact_history_id: updatedRecord.id,
        value: updatedRecord.value || updatedRecord.surgery?.toUpperCase(),
        history_date: updatedRecord.history_date || null,
        remarks: updatedRecord.notes,
        param_group_member_id: updatedRecord.param_group_member_id,
        param_group_member_option_id: updatedRecord.param_group_member_option_id,
      };
      
      console.log('Update payload:', updatePayload);
      console.log('Calling api.opd.updateContactHistory...');
      
      const response = await api.opd.updateContactHistory(updatePayload);
      console.log('API response:', response);
      
      if (response?.data) {
        // Update the UI immediately
        setPreviousSpouseData(prevData => 
          prevData.map(item => item.id === updatedRecord.id ? {
            ...item,
            surgery: updatedRecord.surgery,
            history_date: updatedRecord.history_date,
            notes: updatedRecord.notes,
            param_group_member_id: updatedRecord.param_group_member_id,
            param_group_member_option_id: updatedRecord.param_group_member_option_id,
          } : item)
        );
        
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Spouse surgical history updated successfully'
        });
        
        // Refresh to ensure sync
        try {
          await loadExistingHistories(spouseInfo?.id, 'spouse');
        } catch (loadError) {
          console.error('Error reloading histories after update:', loadError);
        }
      }
    } catch (error) {
      console.error('Error updating spouse surgical history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update spouse surgical history'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler for deleting infertility information (obstetric history)
  const onDeleteInfertilityRecord = async (record) => {
    console.log('Delete infertility record called with:', record);
    
    if (!record || !record.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Unable to identify the infertility record to delete.',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this infertility information? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        
        const deletePayload = {
          contact_id: patientInfo?.id,
          obsteric_history_id: record.id
        };
        
        console.log('Delete payload for infertility record:', deletePayload);
        
        const response = await api.opd.deleteObstetricRecord(deletePayload);
        
        if (response?.data) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Infertility information deleted successfully.',
            timer: 2000,
            showConfirmButton: false
          });
          
          await loadExistingHistories(patientInfo?.id, 'patient');
        }
      } catch (error) {
        console.error('Error deleting infertility record:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to delete infertility information',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handler for deleting miscarriage history records
  const onDeleteObstetricHistory = async (record) => {
    console.log('Delete miscarriage record called with:', record);
    
    if (!record || (!record.id && typeof record !== 'number' && typeof record !== 'string')) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Unable to identify the record to delete. Please try again.',
      });
      return;
    }

    // Show SweetAlert confirmation dialog before deleting
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete this miscarriage history record? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    // Only proceed if user confirmed
    if (result.isConfirmed) {
      try {
        setLoading(true);
        
        // Prepare delete payload for miscarriage history
        let deletePayload;
        
        if (typeof record === 'object' && record !== null) {
          deletePayload = {
            contact_id: record.contact_id || patientInfo?.id,
            miscarriage_history_id: record.id
          };
        } else {
          deletePayload = {
            contact_id: patientInfo?.id,
            miscarriage_history_id: record
          };
        }
        
        console.log('Delete payload for miscarriage history:', deletePayload);
        
        const response = await api.opd.deleteMiscarriageRecord(deletePayload);
        
        if (response?.data) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Miscarriage history deleted successfully.',
            timer: 2000,
            showConfirmButton: false
          });
          
          // Reload the obstetric history data to refresh the UI
          try {
            await loadExistingHistories(patientInfo?.id, 'patient');
          } catch (loadError) {
            console.error('Error reloading histories after delete:', loadError);
          }
        }
      } catch (error) {
        console.error('Error deleting obstetric history:', error);
        console.error('Error details:', error.response?.data);
        
        // Show error message
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to delete obstetric history',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const onEditMiscarriageHistory = async (updatedRecord) => {
    try {
      console.log('onEditMiscarriageHistory called with:', updatedRecord);
      setLoading(true);
      const updatePayload = {
        miscarriage_history_id: updatedRecord.id,
        year_of_miscarriage: updatedRecord.year_of_miscarriage,
        value: updatedRecord.value || updatedRecord.type_of_miscarriage?.toUpperCase(),
        weeks_of_miscarriage: updatedRecord.weeks_of_miscarriage,
        pregnancy_type: updatedRecord.pregnancy_type,
        remarks: updatedRecord.remarks,
        de_done: updatedRecord.de_done,
        param_group_member_id: updatedRecord.param_group_member_id,
        param_group_member_option_id: updatedRecord.param_group_member_option_id,
      };
      
      console.log('Update payload:', updatePayload);
      
      const response = await api.opd.updateMiscarriageHistory(updatePayload);
      console.log('API response:', response);
      
      if (response?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Miscarriage history updated successfully'
        });
        
        // Refresh to ensure sync
        try {
          await loadExistingHistories(patientInfo?.id, 'patient');
        } catch (loadError) {
          console.error('Error reloading histories after update:', loadError);
        }
      }
    } catch (error) {
      console.error('Error updating miscarriage history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update miscarriage history'
      });
    } finally {
      setLoading(false);
    }
  };

  const onMenstrualSubmit = async (historyArray) => {
    try {
      setLoading(true);

      if (!menstrualParams || !Array.isArray(menstrualParams?.param_group_members)) {
        Swal.fire({
          icon: 'error',
          title: 'System Error',
          text: 'Menstrual history parameters not loaded. Please refresh the page and try again.'
        });
        return;
      }

      if (!menstrualContactInfo?.id) {
        Swal.fire({
          icon: 'error',
          title: 'Patient Not Loaded',
          text: 'Patient information not available. Please load patient details first.'
        });
        return;
      }

      // Add contact_id to each history entry (whichever of patient/spouse is female)
      const historyData = historyArray.map(entry => ({
        ...entry,
        contact_id: menstrualContactInfo.id
      }));

      if (historyData.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'No valid data to save. Please fill at least one field.'
        });
        return;
      }

      console.log('Menstrual History Data to send:', historyData);

      // Make API call to save menstrual history
      const response = await api.opd.createContactHistory(historyData);
      if (response?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Menstrual History added successfully'
        });
        // Optionally refresh menstrual history data here
        console.log('Menstrual history saved successfully:', response.data);
      }
    } catch (error) {
      console.error('Error saving menstrual history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: error.response?.data?.message || 'Failed to save menstrual history'
      });
    } finally {
      setLoading(false);
    }
  };

  const onMenstrualDelete = async (id) => {
    try {
      setLoading(true);
      const response = await api.opd.deleteContactHistory(id);
      if (response?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: 'Menstrual history deleted successfully'
        });
        // Optionally refresh menstrual history data here
      }
    } catch (error) {
      console.error('Error deleting menstrual history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: error.response?.data?.message || 'Failed to delete menstrual history'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleObstetricFormSubmit = async (data, type) => {
    console.log(`${type} data saved:`, data);
    
    // Reload the obstetric history data after successful save
    if (type === 'miscarriage' || type === 'infertility') {
      try {
        // Use the existing loadExistingHistories function to reload patient data
        // This will refresh both obstetric_history and miscarriage_histories
        await loadExistingHistories(patientInfo?.id, 'patient');
        
        console.log('Obstetric history data reloaded successfully');
        
        // Show success message indicating data was saved and reloaded
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `${type === 'miscarriage' ? 'Miscarriage' : 'Infertility'} data saved and history updated successfully`
        });
        
      } catch (error) {
        console.error('Error reloading obstetric history:', error);
        Swal.fire({
          icon: 'error',
          title: 'Reload Failed',
          text: `Failed to reload obstetric history: ${error.message}`
        });
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2}>
                    <IconButton
                      onClick={() => navigate(-1)}
                      sx={{
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      Add Patient History - {patientInfo?.name || patientCode}
                    </MDTypography>
                  </Box>
                  
                  {/* Quick Navigation Buttons */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip 
                      title="Manage Investigations" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => navigate(`/contact-investigations/${patientCode}/${coupleCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <ScienceIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip 
                      title="View Prescriptions" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => navigate(`/prescription/patient/${patientCode}/${coupleCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <DescriptionIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip 
                      title="Create Prescription" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => navigate(`/prescription/create/draft/${patientCode}/${coupleCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <AddIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip 
                      title="Infertility Summary" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => navigate(`/infertility-summary/${patientCode}/${coupleCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <AssignmentIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </MDBox>

              {/* Patient Info */}
              {patientInfo && (
                <MDBox p={3}>
                  <Typography variant="h6" gutterBottom>
                    Patient Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Name: <strong>{patientInfo.name}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Phone: <strong>{patientInfo.phone_number || 'N/A'}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Marital Status: <strong>{patientInfo.marital_status || 'N/A'}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Gender: <strong>{patientInfo.gender || 'N/A'}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: '#e1f5fe',
                          border: '1px solid #4fc3f7',
                        }}
                      >
                        <Typography variant="body2" color="#0277bd" sx={{ fontWeight: 'bold' }}>
                          Patient Code
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textPrimary"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {patientInfo.external_code}
                        </Typography>
                      </Box>
                    </Grid>

                    {patientInfo.marital_status === 'married' && patientInfo.married_for && (
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #90caf9',
                          }}
                        >
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                            Married For
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textPrimary"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {patientInfo.married_for}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {patientInfo.marital_status === 'married' && patientInfo.trying_for && (
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: '#f3e5f5',
                            border: '1px solid #ce93d8',
                          }}
                        >
                          <Typography variant="body2" color="#7b1fa2" sx={{ fontWeight: 'bold' }}>
                            Trying For
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textPrimary"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {patientInfo.trying_for}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {patientInfo.occupation && (
                      <Grid item xs={12} md={2}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: '#e8f5e8',
                            border: '1px solid #a5d6a7',
                          }}
                        >
                          <Typography variant="body2" color="#2e7d32" sx={{ fontWeight: 'bold' }}>
                            Occupation
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textPrimary"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {patientInfo.occupation}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: '#fff3e0',
                          border: '1px solid #ffcc02',
                        }}
                      >
                        <Typography variant="body2" color="#f57c00" sx={{ fontWeight: 'bold' }}>
                          Couple Code
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textPrimary"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {patientInfo.couple_code || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <NationalityDisplay
                        nationality={patientInfo.nationality}
                        nationalityId={patientInfo.nationality_id}
                        variant="body2"
                        color="textSecondary"
                        showLabel={true}
                        inline={true}
                        flagSize="16"
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                </MDBox>
              )}

              {/* History Form */}
              <MDBox p={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ backgroundColor: '#8fdde8', p: 2, borderRadius: 2, marginBottom: '20px' }}
                >
                  Patient History
                </Typography>

                <Box>
                  {/* Generic History Accordion */}
                  <Accordion
                    expanded={expandedAccordion === 'generic'}
                    onChange={handleAccordionChange('generic')}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="generic-content"
                      id="generic-header"
                    >
                      <Typography variant="h6">Generic History</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Tabs value={genericTab} onChange={handleGenericTabChange} sx={{ mb: 2 }}>
                        <Tab label="Patient" />
                        <Tab label="Spouse" disabled={!spouseInfo?.id} />
                      </Tabs>
                      {genericTab === 0 && (
                        <form onSubmit={handleGenericSubmitPatient(onGenericSubmitPatient)}>
                          {/* Load Draft Button for Patient */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <LoadDraftButton
                              hasDraftData={patientGenericDraft.hasDraftData}
                              onLoadDraft={async () => {
                                const data = await patientGenericDraft.loadDraftData();
                                if (data) {
                                  resetGenericPatient(data);
                                }
                              }}
                              onClearDraft={async () => {
                                await patientGenericDraft.clearDraft();
                              }}
                              timestamp={patientGenericDraft.getFormattedTimestamp()}
                            />
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Controller
                                name="patient_history"
                                control={genericControlPatient}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Medical History"
                                    error={!!genericErrorsPatient.patient_history}
                                    helperText={genericErrorsPatient.patient_history?.message}
                                    placeholder="Enter medical history..."
                                    height="120px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="family_history"
                                control={genericControlPatient}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Family History"
                                    error={!!genericErrorsPatient.family_history}
                                    helperText={genericErrorsPatient.family_history?.message}
                                    placeholder="Enter family history..."
                                    height="120px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="allergic_to_drugs"
                                control={genericControlPatient}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Allergic to Drugs"
                                    error={!!genericErrorsPatient.allergic_to_drugs}
                                    helperText={genericErrorsPatient.allergic_to_drugs?.message}
                                    placeholder="Enter allergic to drugs information..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="drug_medical_history"
                                control={genericControlPatient}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Drug History"
                                    error={!!genericErrorsPatient.drug_medical_history}
                                    helperText={genericErrorsPatient.drug_medical_history?.message}
                                    placeholder="Enter drug history..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="previous_infertility_treatment"
                                control={genericControlPatient}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Previous Infertility Treatment"
                                    error={!!genericErrorsPatient.previous_infertility_treatment}
                                    helperText={
                                      genericErrorsPatient.previous_infertility_treatment?.message
                                    }
                                    placeholder="Enter previous infertility treatment details..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="comorbidities"
                                control={genericControlPatient}
                                rules={{
                                  validate: (value) =>
                                    !isRichTextEmpty(value) || 'Comorbidities is required',
                                }}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Comorbidities"
                                    required
                                    error={!!genericErrorsPatient.comorbidities}
                                    helperText={genericErrorsPatient.comorbidities?.message}
                                    placeholder="Enter comorbidities (enter NIL if none)..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="notes"
                                control={genericControlPatient}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Notes"
                                    error={!!genericErrorsPatient.notes}
                                    helperText={genericErrorsPatient.notes?.message}
                                    placeholder="Enter additional notes..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <MDBox display="flex" justifyContent="center" gap={2} mt={2}>
                                <button
                                  type="submit"
                                  disabled={loading}
                                  style={{
                                    padding: '10px 24px',
                                    background: '#00798E',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    opacity: loading ? 0.6 : 1,
                                  }}
                                >
                                  Save Generic History
                                </button>
                                <button
                                  type="button"
                                  onClick={handleClearGenericForm}
                                  disabled={loading}
                                  style={{
                                    padding: '10px 24px',
                                    background: 'transparent',
                                    color: '#00798E',
                                    border: '1px solid #00798E',
                                    borderRadius: 4,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    opacity: loading ? 0.6 : 1,
                                  }}
                                >
                                  Clear
                                </button>
                              </MDBox>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                      {genericTab === 1 && (
                        <form onSubmit={handleGenericSubmitSpouse(onGenericSubmitSpouse)}>
                          {/* Load Draft Button for Spouse */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <LoadDraftButton
                              hasDraftData={spouseGenericDraft.hasDraftData}
                              onLoadDraft={async () => {
                                const data = await spouseGenericDraft.loadDraftData();
                                if (data) {
                                  resetGenericSpouse(data);
                                }
                              }}
                              onClearDraft={async () => {
                                await spouseGenericDraft.clearDraft();
                              }}
                              timestamp={spouseGenericDraft.getFormattedTimestamp()}
                            />
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Controller
                                name="patient_history"
                                control={genericControlSpouse}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Medical History"
                                    error={!!genericErrorsSpouse.patient_history}
                                    helperText={genericErrorsSpouse.patient_history?.message}
                                    placeholder="Enter medical history..."
                                    height="120px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="family_history"
                                control={genericControlSpouse}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Family History"
                                    error={!!genericErrorsSpouse.family_history}
                                    helperText={genericErrorsSpouse.family_history?.message}
                                    placeholder="Enter family history..."
                                    height="120px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="allergic_to_drugs"
                                control={genericControlSpouse}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Allergic to Drugs"
                                    error={!!genericErrorsSpouse.allergic_to_drugs}
                                    helperText={genericErrorsSpouse.allergic_to_drugs?.message}
                                    placeholder="Enter allergic to drugs information..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="drug_medical_history"
                                control={genericControlSpouse}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Drug History"
                                    error={!!genericErrorsSpouse.drug_medical_history}
                                    helperText={genericErrorsSpouse.drug_medical_history?.message}
                                    placeholder="Enter drug history..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="previous_infertility_treatment"
                                control={genericControlSpouse}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Previous Infertility Treatment"
                                    error={!!genericErrorsSpouse.previous_infertility_treatment}
                                    helperText={
                                      genericErrorsSpouse.previous_infertility_treatment?.message
                                    }
                                    placeholder="Enter previous infertility treatment details..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="comorbidities"
                                control={genericControlSpouse}
                                rules={{
                                  validate: (value) =>
                                    !isRichTextEmpty(value) || 'Comorbidities is required',
                                }}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Comorbidities"
                                    required
                                    error={!!genericErrorsSpouse.comorbidities}
                                    helperText={genericErrorsSpouse.comorbidities?.message}
                                    placeholder="Enter comorbidities (enter NIL if none)..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="notes"
                                control={genericControlSpouse}
                                render={({ field }) => (
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    label="Notes"
                                    error={!!genericErrorsSpouse.notes}
                                    helperText={genericErrorsSpouse.notes?.message}
                                    placeholder="Enter additional notes..."
                                    height="100px"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <MDBox display="flex" justifyContent="center" gap={2} mt={2}>
                                <button
                                  type="submit"
                                  disabled={loading}
                                  style={{
                                    padding: '10px 24px',
                                    background: '#00798E',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    opacity: loading ? 0.6 : 1,
                                  }}
                                >
                                  Save Spouse History
                                </button>
                                <button
                                  type="button"
                                  onClick={handleClearGenericForm}
                                  disabled={loading}
                                  style={{
                                    padding: '10px 24px',
                                    background: 'transparent',
                                    color: '#00798E',
                                    border: '1px solid #00798E',
                                    borderRadius: 4,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    opacity: loading ? 0.6 : 1,
                                  }}
                                >
                                  Clear
                                </button>
                              </MDBox>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Surgical History Accordion */}
                  <Accordion
                    expanded={expandedAccordion === 'surgical'}
                    onChange={handleAccordionChange('surgical')}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="surgical-content"
                      id="surgical-header"
                    >
                      <Typography variant="h6">Surgical History</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <SurgicalHistoryForm
                        patientId={patientInfo?.id}
                        spouseId={spouseInfo?.id}
                        control={surgicalControl}
                        onSubmit={onSurgicalSubmit}
                        errors={surgicalErrors}
                        loading={loading}
                        handleSubmit={handleSurgicalSubmit}
                        handleClear={handleClearSurgicalForm}
                        onDeletePatient={onDeletePatient}
                        onDeleteSpouse={onDeleteSpouse}
                        onEditPatient={onEditPatient}
                        onEditSpouse={onEditSpouse}
                        opdParams={opdParams}
                        previousPatientData={previousSurgicalData}
                        previousSpouseData={previousSpouseData} // Add this new prop
                        // genericTab={genericTab} // Also pass this to know which tab is active
                      />
                    </AccordionDetails>
                  </Accordion>

                  {/* Obstetric History Accordion */}
                  <Accordion
                    expanded={expandedAccordion === 'obstetric'}
                    onChange={handleAccordionChange('obstetric')}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="obstetric-content"
                      id="obstetric-header"
                    >
                      <Typography variant="h6">Obstetric History</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <ObstetricHistoryForm 
                        patientId={patientInfo?.id}
                        loading={loading}
                        obstetricHistory={obstetricHistory}
                        miscarriageHistories={miscarriageHistories}
                        typeOfMiscarriage={typeOfMiscarriage || []}
                        typeOfPregnancy={typeOfPregnancy || []}
                        onSubmit={handleObstetricFormSubmit}
                        onDeleteInfertility={onDeleteInfertilityRecord}
                        onDeleteMiscarriage={onDeleteObstetricHistory}
                        onEdit={onEditMiscarriageHistory}
                      />
                    </AccordionDetails>
                  </Accordion>

                  {/* Menstrual History Accordion - only relevant when patient or spouse is female */}
                  {menstrualContactInfo && (
                    <Accordion
                      expanded={expandedAccordion === 'menstrual'}
                      onChange={handleAccordionChange('menstrual')}
                      sx={{ mb: 2 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="menstrual-content"
                        id="menstrual-header"
                      >
                        <Typography variant="h6">Menstrual History</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <MenstrualHistoryForm
                          loading={loading}
                          menstrualParams={menstrualParams}
                          onSubmit={onMenstrualSubmit}
                          onDelete={onMenstrualDelete}
                          previousData={[]}
                          contactId={menstrualContactInfo?.id}
                          patientGender={menstrualContactInfo?.gender}
                        />
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              </MDBox>

              {/* Success/Error Messages - Removed: Now using SweetAlert */}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ContactHistories;
